/**
 * Which help topic belongs to which screen.
 *
 * The mapping is data rather than a lookup scattered through the views, so a
 * test can assert that every tab has one — a `?` button opening an empty drawer
 * is worse than no `?` button, because it reads as "there is nothing to say
 * about this screen" rather than as a gap.
 */

import * as upload from "../../help/upload.md";
import * as overview from "../../help/overview.md";
import * as records from "../../help/records.md";
import * as fieldChanges from "../../help/field-changes.md";
import * as explore from "../../help/explore.md";
import * as dataHealth from "../../help/data-health.md";
import * as recovery from "../../help/recovery.md";
import * as ticket from "../../help/ticket.md";
import * as profiles from "../../help/profiles.md";
import * as runs from "../../help/runs.md";
import * as glossary from "../../help/glossary.md";

export type HelpTopic = { id: string; title: string; html: string };

const TOPICS: Record<string, HelpTopic> = {
  upload: { id: "upload", ...upload },
  overview: { id: "overview", ...overview },
  records: { id: "records", ...records },
  "field-changes": { id: "field-changes", ...fieldChanges },
  explore: { id: "explore", ...explore },
  "data-health": { id: "data-health", ...dataHealth },
  recovery: { id: "recovery", ...recovery },
  ticket: { id: "ticket", ...ticket },
  profiles: { id: "profiles", ...profiles },
  runs: { id: "runs", ...runs },
  glossary: { id: "glossary", ...glossary }
};

export const GLOSSARY_TOPIC_ID = "glossary";

/** Every topic, for tests and for the glossary's "see also" list. */
export function listHelpTopics(): HelpTopic[] {
  return Object.values(TOPICS);
}

/**
 * The topic for a screen, or null when there is none.
 *
 * Null rather than a generic placeholder: the caller then renders no `?` at
 * all, which is honest, instead of a button that opens nothing.
 */
export function helpTopic(id: string | null | undefined): HelpTopic | null {
  if (id === null || id === undefined) return null;
  return TOPICS[id] ?? null;
}
